import React, { useState } from "react";
import { CheckLogin } from "../shared/components/CheckLogin";
import { CONTACT_ADMIN, NEW_USER, REFRESH_SESSION, SOMETHING_WENT_WRONG, UNAUTHORIZED_ACCESS, USERS } from "../shared/utils/constants";
import { toTitleCase } from "../shared/utils/helper";
import { Helmet } from "react-helmet";
import { LoadingScreen } from "../shared/components/LoadingScreen";
import { Communication } from "@fluentui/react-teams";
import { Button, Divider, Flex, Header, ProviderConsumer, Text } from "@fluentui/react-northstar";
import { Providers } from "@microsoft/mgt-element/dist/es6/providers/Providers";
import isEmail from 'validator/lib/isEmail';
import { Toolbar } from "../shared/components/Toolbar";
import { t } from "i18next";
import { Breadcrumbs } from "../shared/components/BreadcrumbNavigation";
import { platformService } from "../shared/services/platform.service";
import { searchUsers } from "../shared/cache/PlatformUserCache";
import { useHistory } from "react-router-dom";
import { MobileSettingsView } from "../automation/AutomationTable";
import ConfigCSS from '../config/config.module.css';
import { UserInfoSection, UserInfoField } from "./UserInfoSection";

export interface UserFormState {
    UserName: string;
    DisplayName: string;
    GivenName: string;
    FamilyName: string;
    JobTitle: string;
    CompanyName: string;
    Department: string;
    EmployeeId: string;
    Email: string;
    StreetAddress: string;
    State: string;
    Country: string;
    Office: string;
    City: string;
    Zip: string;
    OfficePhone: string;
    MobilePhone: string;
}

export const AddUserForm = () => {
    
    const platformAPI = new platformService();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [formState, setFormState] = useState<UserFormState>({
        UserName: "",
        DisplayName: "",
        GivenName: "",
        FamilyName: "",
        JobTitle: "",
        CompanyName: "",
        Department: "",
        EmployeeId: "",
        Email: "",
        StreetAddress: "",
        State: "",
        Country: "",
        Office: "",
        City: "",
        Zip: "",
        OfficePhone: "",
        MobilePhone: ""
    });
    const [loadingMessage, setLoadingMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const [errCode, setErrCode] = useState(0);
    const [userNameUniqueError, setUserNameUniqueError] = useState("");

    const isUserNameValid = isEmail(formState.UserName);
    const disableSave = !formState.UserName || !formState.DisplayName || !isUserNameValid || !!userNameUniqueError;
    const userNameError = formState.UserName && !isUserNameValid ? t('users.user-form.invalid-email') : "";

    const validateUserNameUnique = async (value: string) => {
        if (!value || !isEmail(value)) {
            setUserNameUniqueError("");
            return;
        }

        try {
            const existingUser = await searchUsers({ UserName: value, Id: 0 } as PlatformUser, false);
            setUserNameUniqueError(existingUser ? t('users.user-form.existing-email') : "");
        }
        catch {
            setUserNameUniqueError("");
        }
    };

    const setFieldValue = (field: keyof UserFormState, value: string) => {
        if (field === 'UserName') {
            setUserNameUniqueError("");
        }

        setFormState(prevState => ({ ...prevState, [field]: value }));
    };

    const saveUser = async () => {
        setIsLoading(true);
        try {
            const payload = formState;
            const result = await platformAPI.createNewUser(payload);
            if(result?.data?.Id)
                history.push(`/users/${result.data.Id}`);
            setIsError(false);
        } catch (e: any) {
            setIsError(true);
            setErrCode((e['response'] && e['response'].status) || 500);
        } finally {
            setIsLoading(false);
        }
    };

    const errConfig = {
        fields: {
            title: (errCode == 401) ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG,
            desc: CONTACT_ADMIN
        }
    }

    const fetchData = async () => {
        setLoadingMessage(t('users.user-form.getting-user-data'));
        try {
          setIsError(false);
          setIsLoading(false);
        }
        catch (e: any) {
          setIsError(true);
          setErrCode((e['response'] && e['response'].status) || 500);
        }
      };
    
    const loadPage = () => {
        fetchData();
    }

    const getNavigation = (): Breadcrumbs => {
        const items: Breadcrumbs = {
          breadcrumbs: []
        };
    
        if (location.pathname.indexOf('/settings/') >= 0)
          items.breadcrumbs.push({ title: t('users.breadcrumb-settings'), link: '/settings' })
    
        items.breadcrumbs.push({ title: t('users.user-form.users'), link: (items.breadcrumbs.length == 0) ? '/users' : '/settings/users' });
        items.breadcrumbs.push({ title: NEW_USER, link: "" });
        return items;
    };

    const getIdentityFields = (): UserInfoField[] => [
        {
            key: 'UserName',
            label: t('users.user-form.email-address'),
            required: true,
            value: formState.UserName,
            error: userNameError || userNameUniqueError,
            onChange: (value) => setFieldValue('UserName', value),
            onBlur: () => validateUserNameUnique(formState.UserName)
        },
        {
            key: 'DisplayName',
            label: t('users.user-form.display-name'),
            required: true,
            value: formState.DisplayName,
            onChange: (value) => setFieldValue('DisplayName', value)
        },
        {
            key: 'GivenName',
            label: t('users.user-form.first-name'),
            value: formState.GivenName,
            onChange: (value) => setFieldValue('GivenName', value)
        },
        {
            key: 'FamilyName',
            label: t('users.user-form.last-name'),
            value: formState.FamilyName,
            onChange: (value) => setFieldValue('FamilyName', value)
        }
    ];

    const getJobInfoFields = (): UserInfoField[] => [
        {
            key: 'JobTitle',
            label: t('users.user-form.job-title'),
            value: formState.JobTitle,
            onChange: (value) => setFieldValue('JobTitle', value)
        },
        {
            key: 'CompanyName',
            label: t('users.user-form.company-name'),
            value: formState.CompanyName,
            onChange: (value) => setFieldValue('CompanyName', value)
        },
        {
            key: 'Department',
            label: t('users.user-form.department'),
            value: formState.Department,
            onChange: (value) => setFieldValue('Department', value)
        },
        {
            key: 'EmployeeId',
            label: t('users.user-form.employee-id'),
            value: formState.EmployeeId,
            onChange: (value) => setFieldValue('EmployeeId', value)
        }
    ];

    const getContactInfoFields = (): UserInfoField[] => [
        {
            key: 'StreetAddress',
            label: t('users.user-form.street-address'),
            value: formState.StreetAddress,
            onChange: (value) => setFieldValue('StreetAddress', value)
        },
        {
            key: 'City',
            label: t('users.user-form.city'),
            value: formState.City,
            onChange: (value) => setFieldValue('City', value)
        },
        {
            key: 'State',
            label: t('users.user-form.state'),
            value: formState.State,
            onChange: (value) => setFieldValue('State', value)
        },
        {
            key: 'Zip',
            label: t('users.user-form.zip'),
            value: formState.Zip,
            onChange: (value) => setFieldValue('Zip', value)
        },
        {
            key: 'Country',
            label: t('users.user-form.country'),
            value: formState.Country,
            onChange: (value) => setFieldValue('Country', value)
        },
        {
            key: 'Office',
            label: t('users.user-form.office'),
            value: formState.Office,
            onChange: (value) => setFieldValue('Office', value)
        },
        {
            key: 'OfficePhone',
            label: t('users.user-form.office-phone'),
            value: formState.OfficePhone,
            onChange: (value) => setFieldValue('OfficePhone', value)
        },
        {
            key: 'MobilePhone',
            label: t('users.user-form.mobile-phone'),
            value: formState.MobilePhone,
            onChange: (value) => setFieldValue('MobilePhone', value)
        }
    ];

    return (
    <CheckLogin onSignedIn={loadPage}>
        <Helmet>
            <title>{toTitleCase(NEW_USER)} - Tikit</title>
        </Helmet>
        <div className="hidden md:block">
            {(isLoading) && <LoadingScreen message={loadingMessage} />}
            {(!isLoading) &&
            (isError ? (<>
                <Communication {...errConfig} />
                {errCode === 401 && (<Button content={REFRESH_SESSION} primary onClick={e => { Providers.globalProvider.login(); }} />)}
            </>)
                : (
            <ProviderConsumer render={(globalTheme) => (
                <>
                <Toolbar
                globalTheme={globalTheme}
                title={`${USERS} > ${NEW_USER}`}
                disableSave={disableSave}
                breadcrumbs={getNavigation()}
                saveText={t('notification-center.save')}
                saveCommand={saveUser}
                />

                {/* Main Section */}
                <Flex column className={ConfigCSS.configBody} styles={{overflow: "auto"}}>
                <div className="p-6">
                    <Header className={`mt-4`} as="h3" content={<Text weight="semibold" size="larger" content={NEW_USER} />} />
                    <Text content={t('users.user-form.newuser-subheader')} />
                    <Divider className="my-6" />

                    {/* User Info Sections */}
                    <UserInfoSection
                        title="Identity"
                        fields={getIdentityFields()}
                        className="mt-2"
                    />
                    <Divider className="my-6" />

                    <UserInfoSection
                        title={t('users.user-form.job-info')}
                        fields={getJobInfoFields()}
                        className="mt-2"
                    />
                    <Divider className="my-6" />
                    
                    <UserInfoSection
                        title={t('users.user-form.contact-info')}
                        fields={getContactInfoFields()}
                        className="mt-2"
                    />
                </div>
                </Flex>
                </>
            )} />
            ))}
        </div>
        <MobileSettingsView />
    </CheckLogin>
    );
    
}